import Konva from 'konva'
import {VIEW_MODES} from '../../constants/editorConfig'
import {loadImage} from './helpers/cache'
import {createSilhouetteImage} from './helpers/silhouetteImage'
import {SHAPE_PATHS} from '../../../../helpers/shapes/shapePaths'
import {calcScale} from '../../../../helpers/shapes/calcScale'

/**
 * Effective style for a viewMode: base style + styleOverrides[viewMode], without visibleInModes/styleOverrides keys.
 */
export function getEffectiveStyle(model, viewMode = VIEW_MODES.OVERLAY) {
    const raw = model.style || {}
    const base = {...raw}
    delete base.visibleInModes
    delete base.styleOverrides
    const overrides = raw.styleOverrides?.[viewMode]
    return overrides ? {...base, ...overrides} : base
}

/**
 * Resolve text content: prefer model.content.text, fall back to style.text for v1 compat.
 */
function resolveText(model, effectiveStyle) {
    return model.content?.text ?? effectiveStyle.text ?? ''
}

/**
 * Resolve image src: prefer model.content.src, fall back to style.src for v1 compat.
 */
function resolveImageSrc(model, effectiveStyle) {
    return model.content?.src ?? effectiveStyle.src
}

export function createKonvaNode(model, viewMode = VIEW_MODES.OVERLAY) {
    const g = model.geometry
    const effective = getEffectiveStyle(model, viewMode)

    const common = {
        id: model.id,
        x: g.x,
        y: g.y,
        width: g.width,
        height: g.height,
        rotation: g.rotation,
        scaleX: 1,
        scaleY: 1,
        draggable: !model.state?.locked,
        visible: !model.state?.hidden,
    }

    switch (model.type) {
        case 'shape':
            if (viewMode === VIEW_MODES.SILHOUETTE) {
                return new Konva.Rect({
                    ...common,
                    fill: effective.fill ?? 'black',
                    stroke: '#000000',
                    strokeWidth: effective.strokeWidth ?? 0,
                    perfectDrawEnabled: false,
                })
            }
            return new Konva.Rect({
                ...common,
                fill: effective.fill,
                stroke: effective.stroke,
                strokeWidth: effective.strokeWidth ?? 0,
                perfectDrawEnabled: false,
            })

        case 'svg-shape': {
            const shapeType = effective.shapeType ?? model.shapeType
            const shapeDef = SHAPE_PATHS[shapeType]
            if (!shapeDef) {
                console.warn(`[nodeFactory] Unknown shapeType: "${shapeType}", falling back to Rect`)
                return new Konva.Rect({...common, fill: effective.fill, perfectDrawEnabled: false})
            }
            const {scaleX, scaleY} = calcScale(
                {width: shapeDef.rawWidth, height: shapeDef.rawHeight},
                {width: g.width, height: g.height},
            )
            if (viewMode === VIEW_MODES.SILHOUETTE) {
                return new Konva.Path({
                    ...common,
                    data: shapeDef.data,
                    fill: effective.fill ?? 'black',
                    stroke: '#000000',
                    strokeWidth: effective.strokeWidth ?? 0,
                    strokeScaleEnabled: false,
                    scaleX,
                    scaleY,
                    perfectDrawEnabled: false,
                })
            }
            return new Konva.Path({
                ...common,
                data: shapeDef.data,
                fill: effective.fill,
                stroke: effective.stroke,
                strokeWidth: effective.strokeWidth ?? 0,
                strokeScaleEnabled: false,
                scaleX,
                scaleY,
                perfectDrawEnabled: false,
            })
        }

        case 'image': {
            const src = resolveImageSrc(model, effective)
            const imageNode = new Konva.Image({
                ...common,
                id: model.id,
                width: g.width,
                height: g.height,
            })

            if (viewMode === VIEW_MODES.SILHOUETTE) {
                loadImage(src, async (img) => {
                    try {
                        const canvas = await createSilhouetteImage(img, img.width, img.height, {
                            strokeWidth: effective.strokeWidth ?? 0,
                        })
                        if (!imageNode.getLayer()) return
                        imageNode.image(canvas)
                        imageNode.cache()
                        imageNode.getLayer().batchDraw()
                    } catch (err) {
                        console.error('Silhouette image failed:', err)
                    }
                })
            } else {
                loadImage(src, (img) => {
                    imageNode.image(img)
                    imageNode.cache()
                    imageNode.getLayer()?.batchDraw()
                })
            }

            return imageNode
        }

        case 'text': {
            const text = resolveText(model, effective)
            if (viewMode === VIEW_MODES.SILHOUETTE) {
                return new Konva.Text({
                    ...common,
                    text,
                    fontSize: effective.fontSize ?? 16,
                    fontFamily: effective.fontFamily ?? 'Arial',
                    fill: effective.fill ?? 'black',
                    align: effective.align ?? 'left',
                    stroke: '#000000',
                    strokeWidth: effective.strokeWidth ?? 0,
                    perfectDrawEnabled: false,
                })
            }
            return new Konva.Text({
                ...common,
                text,
                fontSize: effective.fontSize ?? 16,
                fontFamily: effective.fontFamily ?? 'Arial',
                fill: effective.fill ?? '#000',
                align: effective.align ?? 'left',
                perfectDrawEnabled: false,
            })
        }

        default:
            throw new Error(`Unsupported type: ${model.type}`)
    }
}

export const ImageNode = (model) => {
    const g = model.geometry
    const src = model.content?.src ?? model.style?.src
    const group = new Konva.Group({
        id: `${model.id}`,
        x: g.x,
        y: g.y,
        draggable: !model.state?.locked,
    })

    const placeholder = new Konva.Rect({
        id: `${model.id}-placeholder`,
        width: g.width,
        height: g.height,
        fill: '#eee',
        stroke: '#ccc',
        dash: [4, 4],
    })

    const imageNode = new Konva.Image({
        id: `${model.id}-image`,
        width: g.width,
        height: g.height,
    })

    group.add(placeholder)
    group.add(imageNode)

    loadImage(src, (img) => {
        imageNode.image(img)
        placeholder.destroy()
        group.getLayer()?.batchDraw()
    })

    return group
}

const TextNode = (model) => {
    const style = model.style
    const g = model.geometry
    const text = model.content?.text ?? style.text ?? ''
    return new Konva.Text({
        id: `${model.id}`,
        x: g.x,
        y: g.y,
        draggable: !model.state?.locked,
        visible: !model.state?.hidden,
        text,
        fontSize: style.fontSize || 16,
        fontFamily: style.fontFamily || 'Arial',
        fill: style.fill || '#000',
        align: style.align || 'left',
    })
}
